// Pages/Admin/AppSetting.jsx
import React, { useEffect, useState } from "react";
import Content from "../../Components/Core/Content";
import FormInput from "../../Components/Core/FormInput";
import InfoTooltip from "../../Components/Core/InfoTooltip";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Button,
  Checkbox,
  Tabs,
  Tab,
  Switch,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Chip,
} from "@mui/material";
import TabScrollButton from "@mui/material/TabScrollButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../Components/Core/Loader";
import Notification from "../../Components/Core/Notification";
import { CheckRole } from "../../Services/AuthService";
import {
  getSettings,
  getWorkingDays,
  updateSettings,
  updateWorkingDays,
  getExceptionDays,
  addExceptionDay,
  deleteExceptionDay,
} from "../../Services/SettingService";
import {
  getTenantContent,
  updateTenantContent,
} from "../../Services/TennantContentService";
import { useTenantContent } from "../../Context/TenantContentContext";
import ImageUploadField from "../../Components/Core/ImageUploadField";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import TuneIcon from "@mui/icons-material/Tune";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PaymentsIcon from "@mui/icons-material/Payments";
import BuildIcon from "@mui/icons-material/Build";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ChatIcon from "@mui/icons-material/Chat";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaletteIcon from "@mui/icons-material/Palette";
import PersonIcon from "@mui/icons-material/Person";
import ShareIcon from "@mui/icons-material/Share";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import SchoolIcon from "@mui/icons-material/School";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import WorkIcon from "@mui/icons-material/Work";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CollectionsIcon from "@mui/icons-material/Collections";
import ForumIcon from "@mui/icons-material/Forum";
import AssignmentIcon from "@mui/icons-material/Assignment";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import "./AppSetting.css";

const contentAccordionSx = {
  mb: 1.5,
  borderRadius: "16px !important",
  overflow: "hidden",
  "&:before": { display: "none" },
  boxShadow: "none",
  border: "1px solid rgba(5, 50, 81, 0.08)",
  backgroundColor: "#fff",
  "&.Mui-expanded": {
    boxShadow: "0 10px 28px rgba(5, 50, 81, 0.08)",
    borderColor: "rgba(124, 200, 144, 0.4)",
  },
};

const contentAccordionSummarySx = {
  minHeight: 72,
  px: { xs: 1.5, md: 2 },
  background:
    "linear-gradient(90deg, rgba(5,50,81,0.035), rgba(124,200,144,0.08))",
  "& .MuiAccordionSummary-content": {
    margin: "12px 0",
    alignItems: "center",
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "#053251",
  },
};

const AccordionTitle = ({ icon: Icon, title, hint }) => (
  <div className="settings-acc-head">
    <span className="settings-acc-icon" aria-hidden="true">
      <Icon fontSize="small" />
    </span>
    <div className="settings-acc-copy">
      <h5>{title}</h5>
      {hint ? <p>{hint}</p> : null}
    </div>
  </div>
);

const SettingsSectionTitle = ({ icon: Icon, children }) => (
  <h5 className="settings-section-title iran-sans">
    <span className="settings-section-icon" aria-hidden="true">
      <Icon fontSize="small" />
    </span>
    {children}
  </h5>
);

const RtlTabScrollButton = React.forwardRef(function RtlTabScrollButton(
  props,
  ref,
) {
  const { direction, ...other } = props;
  return (
    <TabScrollButton
      ref={ref}
      {...other}
      direction={direction === "left" ? "right" : "left"}
    />
  );
});

const tabSx = {
  "& .MuiTab-root": {
    fontSize: "14px",
    fontWeight: 600,
    minHeight: 46,
    borderRadius: "12px",
    color: "#053251",
    textTransform: "none",
    fontFamily: "MyPersianFont, sans-serif",
    letterSpacing: 0,
    "&.Mui-selected": {
      color: "#053251",
      backgroundColor: "rgba(124, 200, 144, 0.2)",
    },
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiTabs-scrollButtons": {
    color: "#7cc890",
    "@media (max-width: 768px)": {
      display: "flex",
    },
  },
  "& .MuiTabs-flexContainer": {
    gap: "6px",
  },
};

const AppSetting = () => {
  const { refreshContent } = useTenantContent();
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const [workingDays, setWorkingDays] = useState([]);
  const [initialWorkingDays, setInitialWorkingDays] = useState([]);
  const [exceptionDays, setExceptionDays] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [exceptionReason, setExceptionReason] = useState("");

  const [tenantContent, setTenantContent] = useState(null);
  const [initialTenantContent, setInitialTenantContent] = useState(null);

  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    document.title = "تنظیمات سیستم";
    fetchSettings();
    fetchWorkingDays();
    fetchExceptionDays();
    fetchTenantContent();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await getSettings();
      setSettings(data);
      setInitialSettings(data);
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage("خطا در دریافت تنظیمات");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkingDays = async () => {
    setLoading(true);
    try {
      const data = await getWorkingDays();
      setWorkingDays(data);
      setInitialWorkingDays(JSON.parse(JSON.stringify(data)));
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage("خطا در دریافت روزهای کاری");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const toYMD = (d) => {
    if (!d) return "";
    return d.convert(gregorian, gregorian_en).format("YYYY-MM-DD");
  };

  const formatYmdToFa = (ymd) => {
    if (!ymd) return "";
    const date = new Date(`${ymd}T00:00:00`);
    return date.toLocaleDateString("fa-IR");
  };

  const removeSelectedDate = (dateToRemove) => {
    setSelectedDates((prev) => prev.filter((item) => item !== dateToRemove));
  };

  const exceptionSet = new Set(
    exceptionDays.map((x) => {
      const d = new Date(x.date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }),
  );

  const mapDays = ({ date }) => {
    const ymd = date.convert(gregorian, gregorian_en).format("YYYY-MM-DD");
    if (exceptionSet.has(ymd)) {
      return {
        disabled: true,
        style: { background: "#7cc890", color: "#fff", borderRadius: 8 },
      };
    }
    return {};
  };

  const fetchExceptionDays = async () => {
    setLoading(true);
    try {
      const data = await getExceptionDays();
      setExceptionDays(data);
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage("خطا در دریافت روزهای استثنا");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantContent = async () => {
    setLoading(true);
    try {
      const data = await getTenantContent();
      setTenantContent(data);
      setInitialTenantContent(JSON.parse(JSON.stringify(data)));
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage("خطا در دریافت محتوای سایت");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const settingsChangeDetection = () =>
    JSON.stringify(settings) !== JSON.stringify(initialSettings);

  const workingDaysChangeDetection = () =>
    JSON.stringify(workingDays) !== JSON.stringify(initialWorkingDays);

  // ✅ تشخیص تغییرات محتوای سایت
  const tenantContentChangeDetection = () =>
    JSON.stringify(tenantContent) !== JSON.stringify(initialTenantContent);

  const formatTimeToTimeSpan = (time) => {
    if (!time) return "00:00:00";
    if (typeof time === "string" && time.includes(":")) {
      const parts = time.split(":");
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:00`;
      }
    }
    return time;
  };

  const submitSettingsHandler = async () => {
    try {
      setLoading(true);
      await updateSettings(settings);
      setSnackbarSeverity("success");
      setSnackbarMessage("تنظیمات با موفقیت ذخیره شد");
      setInitialSettings(settings);
      setOpenSnackbar(true);
      // Refresh tenant content (includes feature flags) so changes apply immediately
      refreshContent?.();
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error?.message || "خطا در ذخیره‌سازی تنظیمات");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const submitWorkingDaysHandler = async () => {
    try {
      setLoading(true);
      const payload = workingDays.map((day) => ({
        day: day.day,
        dayName: day.dayName,
        startTime: formatTimeToTimeSpan(day.startTime),
        endTime: formatTimeToTimeSpan(day.endTime),
        intervalMinutes: parseInt(day.intervalMinutes),
        description: day.description || "",
        isActive: day.isActive,
      }));
      await updateWorkingDays(payload);

      setSnackbarSeverity("success");
      setSnackbarMessage("روزهای کاری با موفقیت ذخیره شد");
      setInitialWorkingDays(JSON.parse(JSON.stringify(workingDays)));
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error?.message || "خطا در ذخیره‌سازی");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // ✅ ذخیره محتوای سایت
  const submitTenantContentHandler = async () => {
    try {
      setLoading(true);
      await updateTenantContent(tenantContent);
      setSnackbarSeverity("success");
      setSnackbarMessage("محتوای سایت با موفقیت ذخیره شد");
      setInitialTenantContent(JSON.parse(JSON.stringify(tenantContent)));
      setOpenSnackbar(true);
      refreshContent?.();

      // ✅ رفرش صفحه برای اعمال تغییرات (اختیاری)
      // window.location.reload();
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error?.message || "خطا در ذخیره‌سازی محتوا");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddException = async () => {
    if (selectedDates.length === 0) {
      setSnackbarSeverity("error");
      setSnackbarMessage("لطفاً حداقل یک تاریخ انتخاب کنید");
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        dates: selectedDates.map((date) => `${date}T00:00:00`),
        reason: exceptionReason,
      };

      await addExceptionDay(payload);
      await fetchExceptionDays();
      setSelectedDates([]);
      setExceptionReason("");
      setSnackbarSeverity("success");
      setSnackbarMessage("روزهای استثنا با موفقیت اضافه شدند");
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error?.message || "خطا در افزودن روز استثنا");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteException = async (id) => {
    try {
      setLoading(true);
      await deleteExceptionDay(id);
      await fetchExceptionDays();
      setSnackbarSeverity("success");
      setSnackbarMessage("روز استثنا با موفقیت حذف شد");
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error?.message || "خطا در حذف روز استثنا");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const moveItem = (array, index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= array.length) return array;

    const newArray = [...array];
    [newArray[index], newArray[newIndex]] = [
      newArray[newIndex],
      newArray[index],
    ];
    return newArray;
  };

  const moveEducationItem = (index, direction) => {
    const updated = moveItem(tenantContent.educationItems, index, direction);
    setTenantContent((prev) => ({ ...prev, educationItems: updated }));
  };

  const moveAcademicDegree = (index, direction) => {
    const updated = moveItem(tenantContent.academicDegrees, index, direction);
    setTenantContent((prev) => ({ ...prev, academicDegrees: updated }));
  };

  const moveResponsibility = (index, direction) => {
    const updated = moveItem(tenantContent.responsibilities, index, direction);
    setTenantContent((prev) => ({ ...prev, responsibilities: updated }));
  };

  const asLocalizedItem = (item) =>
    typeof item === "string"
      ? { textFa: item, textEn: "" }
      : item || { textFa: "", textEn: "" };

  const updateLocalizedListItem = (field, index, key, value) => {
    setTenantContent((prev) => {
      const updated = [...(prev[field] || [])].map(asLocalizedItem);
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, [field]: updated };
    });
  };

  const moveService = (index, direction) => {
    const updated = moveItem(tenantContent.services, index, direction);
    setTenantContent((prev) => ({ ...prev, services: updated }));
  };

  const movePortfolioItem = (index, direction) => {
    const items = [...tenantContent.portfolioItems];
    if (direction === "up" && index > 0) {
      [items[index - 1], items[index]] = [items[index], items[index - 1]];
    } else if (direction === "down" && index < items.length - 1) {
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
    }
    setTenantContent((prev) => ({ ...prev, portfolioItems: items }));
  };

  const moveTestimonial = (index, direction) => {
    const items = [...tenantContent.testimonials];
    if (direction === "up" && index > 0) {
      [items[index - 1], items[index]] = [items[index], items[index - 1]];
    } else if (direction === "down" && index < items.length - 1) {
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
    }
    setTenantContent((prev) => ({ ...prev, testimonials: items }));
  };

  const moveReserveModalItem = (index, direction) => {
    const updated = moveItem(
      tenantContent.reserveInfoModalContent,
      index,
      direction,
    );
    setTenantContent((prev) => ({
      ...prev,
      reserveInfoModalContent: updated,
    }));
  };

  const moveConsultationModalItem = (index, direction) => {
    const updated = moveItem(
      tenantContent.consultationPaymentModalContent,
      index,
      direction,
    );
    setTenantContent((prev) => ({
      ...prev,
      consultationPaymentModalContent: updated,
    }));
  };

  const moveHomeModalItem = (index, direction) => {
    const updated = moveItem(
      tenantContent.homeInitialModalContent,
      index,
      direction,
    );
    setTenantContent((prev) => ({
      ...prev,
      homeInitialModalContent: updated,
    }));
  };

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleDayToggle = (index) => {
    const updated = [...workingDays];
    updated[index].isActive = !updated[index].isActive;
    setWorkingDays(updated);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...workingDays];
    updated[index][field] = value;
    setWorkingDays(updated);
  };

  // ✅ هندلرهای محتوای سایت
  const handleContentChange = (field, value) => {
    setTenantContent((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUploaded = (field, url) => {
    setTenantContent((prev) => ({ ...prev, [field]: url }));
    setInitialTenantContent((prev) => ({ ...prev, [field]: url }));
    refreshContent?.();
    setSnackbarMessage("تصویر ذخیره شد و در سایت نمایش داده می‌شود.");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
  };

  const handleServiceChange = (index, field, value) => {
    const updated = [...tenantContent.services];
    updated[index][field] = value;
    setTenantContent((prev) => ({ ...prev, services: updated }));
  };

  const handleAddService = () => {
    const newService = {
      title: "",
      description: "",
      fullDescription: "",
      iconUrl: "",
    };
    setTenantContent((prev) => ({
      ...prev,
      services: [...(prev.services || []), newService],
    }));
  };

  const handleDeleteService = (index) => {
    const updated = tenantContent.services.filter((_, i) => i !== index);
    setTenantContent((prev) => ({ ...prev, services: updated }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const intervalOptions = [5, 10, 15, 20, 30, 45, 60, 75, 90, 120];

  return (
    <Content>
      {loading ? (
        <Loader />
      ) : (
        <div className="container app-setting-page mb-5">
          <header className="app-setting-hero iran-sans">
            <div className="app-setting-hero-copy">
              <p className="app-setting-kicker">پنل مدیریت</p>
              <h1>تنظیمات سیستم</h1>
            </div>
          </header>

          <div className="app-setting-tabs-wrap">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              ScrollButtonComponent={RtlTabScrollButton}
              aria-label="بخش‌های تنظیمات سیستم"
              TabScrollButtonProps={{
                sx: {
                  color: "#7cc890",
                  minWidth: 40,
                  "&.Mui-disabled": { opacity: 0.25 },
                },
              }}
              sx={tabSx}
            >
              <Tab
                icon={<TuneIcon />}
                iconPosition="start"
                label="تنظیمات عمومی"
              />
              <Tab
                icon={<CalendarMonthIcon />}
                iconPosition="start"
                label="روزهای کاری"
              />
              <Tab
                icon={<EventBusyIcon />}
                iconPosition="start"
                label="روزهای استثنا"
              />
              <Tab
                icon={<WebAssetIcon />}
                iconPosition="start"
                label="محتوای سایت"
              />
            </Tabs>
            <p className="app-setting-scroll-hint iran-sans">
              تب‌ها در این ردیف اسکرول می‌شوند. برای دیدن بقیه، بکشید یا از
              فلش‌های کنار استفاده کنید.
            </p>
          </div>

          {/* Tab 1: System Settings */}
          {activeTab === 0 && (
            <>
              {/* ===== بخش 0: فعال‌سازی  ===== */}
              <div className="settings-section">
                <SettingsSectionTitle icon={PowerSettingsNewIcon}>
                  فعال‌سازی قابلیت‌های اصلی
                </SettingsSectionTitle>
                <div className="row">
                  <div className="col-md-4 col-12 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white d-flex align-items-center">
                      <Checkbox
                        checked={settings.isReservationPageEnabled !== false}
                        onChange={(e) =>
                          handleSettingChange(
                            "isReservationPageEnabled",
                            e.target.checked,
                          )
                        }
                        color="primary"
                        size="medium"
                      />
                      <div className="flex-grow-1">
                        <span className="fw-semibold">رزرو نوبت</span>
                        <InfoTooltip
                          text="فعال یا غیرفعال کردن دسترسی به صفحه رزرو نوبت."
                          placement="right"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 col-12 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white d-flex align-items-center">
                      <Checkbox
                        checked={settings.isConsultationPageEnabled !== false}
                        onChange={(e) =>
                          handleSettingChange(
                            "isConsultationPageEnabled",
                            e.target.checked,
                          )
                        }
                        color="primary"
                        size="medium"
                      />
                      <div className="flex-grow-1">
                        <span className="fw-semibold">مشاوره آنلاین</span>
                        <InfoTooltip
                          text="فعال یا غیرفعال کردن دسترسی به صفحه مشاوره."
                          placement="right"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 col-12 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white d-flex align-items-center">
                      <Checkbox
                        checked={settings.isBlogPageEnabled !== false}
                        onChange={(e) =>
                          handleSettingChange(
                            "isBlogPageEnabled",
                            e.target.checked,
                          )
                        }
                        color="primary"
                        size="medium"
                      />
                      <div className="flex-grow-1">
                        <span className="fw-semibold">مقالات و بلاگ</span>
                        <InfoTooltip
                          text="فعال یا غیرفعال کردن دسترسی به صفحه مقالات."
                          placement="right"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== بخش 1: تنظیمات مالی (هر چک‌باکس کنار تنظیم مرتبط) ===== */}
              <div className="settings-section mt-5">
                <SettingsSectionTitle icon={PaymentsIcon}>
                  تنظیمات مالی
                </SettingsSectionTitle>

                <div className="row">
                  {/* بیعانه رزرو: چک‌باکس + مبلغ در یک بلوک */}
                  <div className="col-md-6 col-12 mb-4">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white h-100">
                      <div className="d-flex align-items-start mb-3">
                        <Checkbox
                          checked={
                            settings.isReservationDepositeActive || false
                          }
                          onChange={(e) =>
                            handleSettingChange(
                              "isReservationDepositeActive",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="medium"
                          sx={{ mt: -0.5 }}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <span className="fw-semibold">
                              بیعانه برای رزرو نوبت
                            </span>
                            <InfoTooltip
                              text="بیماران باید برای رزرو نوبت مبلغی را به عنوان بیعانه پرداخت کنند."
                              placement="right"
                            />
                          </div>
                          {settings.isReservationDepositeActive && (
                            <div className="mt-2">
                              <label className="form-label iran-sans mb-1 small text-muted">
                                مبلغ رزرو (تومان)
                              </label>
                              <FormInput
                                className="form-blue"
                                onChange={(e) =>
                                  handleSettingChange(
                                    "reservationFee",
                                    e.target.value,
                                  )
                                }
                                value={settings.reservationFee || ""}
                                for="ReservationFee"
                                type="number"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* مبلغ مشاوره */}
                  <div className="col-md-6 col-12 mb-4">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white h-100">
                      <div className="d-flex align-items-center mb-1">
                        <label className="form-label iran-sans mb-0 fw-semibold">
                          مبلغ مشاوره (تومان)
                        </label>
                        <InfoTooltip text="هزینه مشاوره آنلاین که بیمار برای شروع چت با پزشک باید پرداخت کند." />
                      </div>
                      <FormInput
                        className="form-blue"
                        onChange={(e) =>
                          handleSettingChange("consultationFee", e.target.value)
                        }
                        value={settings.consultationFee || ""}
                        for="consultationFee"
                        type="number"
                      />
                    </div>
                  </div>

                  {/* مشاوره مجدد: چک‌باکس + مبلغ + روز در یک بلوک */}
                  <div className="col-md-6 col-12 mb-4">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white h-100">
                      <div className="d-flex align-items-start mb-3">
                        <Checkbox
                          checked={
                            settings.isRevisedConsultationFeatureEnabled ||
                            false
                          }
                          onChange={(e) =>
                            handleSettingChange(
                              "isRevisedConsultationFeatureEnabled",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="medium"
                          sx={{ mt: -0.5 }}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <span className="fw-semibold">
                              مشاوره مجدد با تخفیف
                            </span>
                            <InfoTooltip
                              text="بیماران می‌توانند در بازه زمانی مشخص با تخفیف مشاوره مجدد بگیرند."
                              placement="right"
                            />
                          </div>
                          {settings.isRevisedConsultationFeatureEnabled && (
                            <div className="row g-2 mt-1">
                              <div className="col-12 col-md-6">
                                <label className="form-label iran-sans mb-1 small text-muted">
                                  مبلغ مشاوره مجدد (تومان)
                                </label>
                                <FormInput
                                  className="form-blue"
                                  onChange={(e) =>
                                    handleSettingChange(
                                      "revisedConsultationFee",
                                      e.target.value,
                                    )
                                  }
                                  value={settings.revisedConsultationFee || ""}
                                  for="RevisedConsultationFee"
                                  type="number"
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <label className="form-label iran-sans mb-1 small text-muted">
                                  بازه زمانی (روز)
                                </label>
                                <FormInput
                                  className="form-blue"
                                  onChange={(e) =>
                                    handleSettingChange(
                                      "revisedConsultationDays",
                                      e.target.value,
                                    )
                                  }
                                  value={settings.revisedConsultationDays || ""}
                                  for="RevisedConsultationDays"
                                  type="number"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {CheckRole(["Admin"]) && (
                <div className="settings-section mt-4">
                  <SettingsSectionTitle icon={BuildIcon}>
                    تنظیمات فنی
                  </SettingsSectionTitle>

                  <div className="row">
                    <div className="col-md-4 col-11 mb-3">
                      <FormInput
                        className="form-blue"
                        name="شناسه پزشک (Doctor ID)"
                        onChange={(e) =>
                          handleSettingChange("doctorId", e.target.value)
                        }
                        value={settings.doctorId || ""}
                        for="DoctorId"
                        type="number"
                      />
                    </div>

                    <div className="col-md-4 col-11 mb-3">
                      <FormInput
                        className="form-blue"
                        name="شناسه درگاه پرداخت (Merchant ID)"
                        onChange={(e) =>
                          handleSettingChange("merchantId", e.target.value)
                        }
                        value={settings.merchantId || ""}
                        for="MerchantId"
                        dir="ltr"
                      />
                    </div>

                    <div className="col-md-4 col-11 mb-3">
                      <FormInput
                        className="form-blue"
                        name="شماره ترمینال سپ (بانک سامان)"
                        onChange={(e) =>
                          handleSettingChange("sepTerminalId", e.target.value)
                        }
                        value={settings.sepTerminalId || ""}
                        for="SepTerminalId"
                        dir="ltr"
                      />
                    </div>

                    <div className="col-md-4 col-11 mb-3">
                      <label className="form-label iran-sans mb-1 text-muted small">
                        روش درگاه پرداخت
                      </label>
                      <FormControl fullWidth size="small">
                        <Select
                          value={Number(settings.paymentGatewayMethod) || 1}
                          onChange={(e) =>
                            handleSettingChange(
                              "paymentGatewayMethod",
                              Number(e.target.value),
                            )
                          }
                          className="iran-sans"
                          sx={{
                            borderRadius: "12px",
                            backgroundColor: "#fbfcfd",
                            fontSize: "0.88rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(5, 50, 81, 0.2)",
                            },
                          }}
                        >
                          <MenuItem value={1} className="iran-sans">
                            اشتراکی (داک‌لینک - زرین‌پال و سپ)
                          </MenuItem>
                          <MenuItem value={2} className="iran-sans">
                            اختصاصی پزشک (زرین‌پال اختصاصی)
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </div>

                    <div className="col-md-4 col-11 mb-3">
                      <FormInput
                        className="form-blue"
                        name="URL کال‌بک مشاوره"
                        onChange={(e) =>
                          handleSettingChange(
                            "consultationCallbackUrl",
                            e.target.value,
                          )
                        }
                        value={settings.consultationCallbackUrl || ""}
                        for="ConsultationCallbackUrl"
                        dir="ltr"
                      />
                    </div>

                    <div className="col-md-4 col-11 mb-3">
                      <FormInput
                        className="form-blue"
                        name="URL کال‌بک رزرو"
                        onChange={(e) =>
                          handleSettingChange(
                            "reservationCallbackUrl",
                            e.target.value,
                          )
                        }
                        value={settings.reservationCallbackUrl || ""}
                        for="ReservationCallbackUrl"
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ===== بخش 3: تنظیمات رزرو ===== */}
              <div className="settings-section mt-5">
                <SettingsSectionTitle icon={EventAvailableIcon}>
                  تنظیمات رزرو
                </SettingsSectionTitle>

                <div className="row">
                  <div className="col-md-4 col-11 mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <label className="form-label iran-sans mb-0">
                        حداکثر ماه‌های قابل نمایش
                      </label>
                      <InfoTooltip text="بیماران تا چند ماه آینده می‌توانند نوبت رزرو کنند." />
                    </div>
                    <FormInput
                      className="form-blue"
                      onChange={(e) =>
                        handleSettingChange("maxAllowedMonth", e.target.value)
                      }
                      value={settings.maxAllowedMonth || ""}
                      for="MaxAllowedMonth"
                      type="number"
                    />
                  </div>

                  <div className="col-md-4 col-11 mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <label className="form-label iran-sans mb-0">
                        شروع رزرو از چند ماه آینده
                      </label>
                      <InfoTooltip text="عدد 0 = ماه جاری فعال | عدد 1 = از ماه بعد شروع می‌شود" />
                    </div>
                    <FormInput
                      className="form-blue"
                      onChange={(e) =>
                        handleSettingChange("skipMonth", e.target.value)
                      }
                      value={settings.skipMonth || ""}
                      for="skipMonth"
                      type="number"
                      placeHolder="0"
                    />
                  </div>

                  {/* محدودیت رزرو: چک‌باکس + روز در یک بلوک */}
                  <div className="col-md-4 col-12 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white h-100">
                      <div className="d-flex align-items-start mb-2">
                        <Checkbox
                          checked={
                            settings.isReservationRestrictionFeatureEnabled ||
                            false
                          }
                          onChange={(e) =>
                            handleSettingChange(
                              "isReservationRestrictionFeatureEnabled",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="medium"
                          sx={{ mt: -0.5 }}
                        />
                        <div className="flex-grow-1">
                          <span className="fw-semibold">محدودیت رزرو</span>
                          <InfoTooltip
                            text="محدود کردن تعداد رزرو بیماران در بازه زمانی مشخص."
                            placement="right"
                          />
                        </div>
                      </div>
                      {settings.isReservationRestrictionFeatureEnabled && (
                        <div className="mt-2">
                          <label className="form-label iran-sans mb-1 small text-muted">
                            بازه محدودیت (روز)
                          </label>
                          <FormInput
                            className="form-blue"
                            onChange={(e) =>
                              handleSettingChange(
                                "reservationRestrictionDays",
                                e.target.value,
                              )
                            }
                            value={settings.reservationRestrictionDays || ""}
                            for="ReservationRestrictionDays"
                            type="number"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* امکان حذف رزرو: چک‌باکس + تعداد روز در یک بلوک */}
                  <div className="col-md-4 col-12 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white h-100">
                      <div className="d-flex align-items-start mb-2">
                        <Checkbox
                          checked={settings.isUserCanDeleteReservation || false}
                          onChange={(e) =>
                            handleSettingChange(
                              "isUserCanDeleteReservation",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="medium"
                          sx={{ mt: -0.5 }}
                        />
                        <div className="flex-grow-1">
                          <span className="fw-semibold">
                            امکان حذف رزرو توسط بیمار
                          </span>
                          <InfoTooltip
                            text="بیماران می‌توانند نوبت‌های خود را قبل از موعد مقرر لغو کنند."
                            placement="right"
                          />
                        </div>
                      </div>
                      {settings.isUserCanDeleteReservation && (
                        <div className="mt-2">
                          <label className="form-label iran-sans mb-1 small text-muted">
                            حداکثر روز باقی‌مانده برای حذف
                          </label>
                          <FormControl fullWidth>
                            <Select
                              value={settings.maxReservationDeletableDays || 1}
                              onChange={(e) =>
                                handleSettingChange(
                                  "maxReservationDeletableDays",
                                  e.target.value,
                                )
                              }
                              sx={{
                                height: "37px",
                                fontFamily:
                                  "iran-sans, MyPersianFont, sans-serif",
                              }}
                            >
                              <MenuItem value={1}>1 روز</MenuItem>
                              <MenuItem value={2}>2 روز</MenuItem>
                              <MenuItem value={3}>3 روز</MenuItem>
                              <MenuItem value={5}>5 روز</MenuItem>
                              <MenuItem value={7}>1 هفته</MenuItem>
                              <MenuItem value={14}>2 هفته</MenuItem>
                              <MenuItem value={30}>1 ماه</MenuItem>
                            </Select>
                          </FormControl>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== بخش 4: تنظیمات مشاوره (چک‌باکس + ساعت در یک بلوک) ===== */}
              <div className="settings-section mt-5">
                <SettingsSectionTitle icon={ChatIcon}>
                  تنظیمات مشاوره
                </SettingsSectionTitle>

                <div className="row">
                  <div className="col-md-6 col-12 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white h-100">
                      <div className="d-flex align-items-start mb-2">
                        <Checkbox
                          checked={
                            settings.isUnreadSmsReminderServiceActive || false
                          }
                          onChange={(e) =>
                            handleSettingChange(
                              "isUnreadSmsReminderServiceActive",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="medium"
                          sx={{ mt: -0.5 }}
                        />
                        <div className="flex-grow-1">
                          <span className="fw-semibold">
                            یادآوری پیامک مشاوره‌های خوانده نشده
                          </span>
                          <InfoTooltip
                            text="ارسال پیامک یادآوری برای پیام‌های پاسخ داده نشده."
                            placement="right"
                          />
                        </div>
                      </div>
                      {settings.isUnreadSmsReminderServiceActive && (
                        <div className="mt-2">
                          <label className="form-label iran-sans mb-1 small text-muted">
                            یادآوری بعد از گذشت (ساعت)
                          </label>
                          <FormInput
                            className="form-blue"
                            onChange={(e) =>
                              handleSettingChange(
                                "unreadSmsReminderServiceActiveHours",
                                e.target.value,
                              )
                            }
                            value={
                              settings.unreadSmsReminderServiceActiveHours || ""
                            }
                            for="unreadSmsReminderServiceActiveHours"
                            type="number"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== بخش 5: دسترسی آپلود فایل ===== */}
              <div className="settings-section mt-5">
                <SettingsSectionTitle icon={AttachFileIcon}>
                  دسترسی بیماران به ارسال فایل
                </SettingsSectionTitle>

                <div className="row">
                  <div className="col-md-4 col-11 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white">
                      <div className="d-flex align-items-center">
                        <Checkbox
                          checked={settings.allowPatientImageUpload || false}
                          onChange={(e) =>
                            handleSettingChange(
                              "allowPatientImageUpload",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="medium"
                        />
                        <span className="fw-semibold">ارسال تصویر</span>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 col-11 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white">
                      <div className="d-flex align-items-center">
                        <Checkbox
                          checked={settings.allowPatientPdfUpload || false}
                          onChange={(e) =>
                            handleSettingChange(
                              "allowPatientPdfUpload",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="medium"
                        />
                        <span className="fw-semibold">ارسال فایل PDF</span>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 col-11 mb-3">
                    <div className="feature-toggle-card p-3 rounded shadow-sm bg-white">
                      <div className="d-flex align-items-center">
                        <Checkbox
                          checked={settings.allowPatientVoiceUpload || false}
                          onChange={(e) =>
                            handleSettingChange(
                              "allowPatientVoiceUpload",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="medium"
                        />
                        <span className="fw-semibold">ارسال صوت</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* دکمه ذخیره */}
              <div className="app-setting-save">
                <Button
                  variant="contained"
                  className="px-5 btn-blue iran-sans"
                  onClick={submitSettingsHandler}
                  disabled={!settingsChangeDetection()}
                >
                  <SaveIcon sx={{ fontSize: 18, ml: 1 }} />
                  <span>ذخیره تنظیمات</span>
                </Button>
              </div>
            </>
          )}

          {/* Tab 2: Working Days */}
          {activeTab === 1 && (
            <>
              <div className="row mt-2">
                <div className="col-12">
                  <div className="card app-setting-panel">
                    <div className="card-body">
                      <SettingsSectionTitle icon={CalendarMonthIcon}>
                        برنامه هفتگی دسترسی
                      </SettingsSectionTitle>

                      {workingDays.map((day, index) => (
                        <div
                          key={day.day}
                          className="working-day-row"
                          style={{
                            borderBottom:
                              index !== workingDays.length - 1
                                ? "1px solid rgba(5, 50, 81, 0.08)"
                                : "none",
                          }}
                        >
                          <div className="row align-items-center mb-3">
                            <div className="col-6">
                              <h6 className="fw-bold mb-0 color-blue iran-sans">
                                {day.dayName}
                              </h6>
                            </div>
                            <div className="col-6 text-end">
                              <Switch
                                checked={day.isActive}
                                onChange={() => handleDayToggle(index)}
                                color="primary"
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "#7cc890",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                    {
                                      backgroundColor: "#7cc890",
                                    },
                                }}
                              />
                            </div>
                          </div>

                          {day.isActive ? (
                            <div className="row">
                              <div className="col-md-4 col-12 mb-3">
                                <FormInput
                                  className="form-blue"
                                  name="زمان شروع"
                                  onChange={(e) =>
                                    handleFieldChange(
                                      index,
                                      "startTime",
                                      e.target.value,
                                    )
                                  }
                                  value={day.startTime}
                                  type="time"
                                  for={`startTime-${day.day}`}
                                />
                              </div>
                              <div className="col-md-4 col-12 mb-3">
                                <FormInput
                                  className="form-blue"
                                  name="زمان پایان"
                                  onChange={(e) =>
                                    handleFieldChange(
                                      index,
                                      "endTime",
                                      e.target.value,
                                    )
                                  }
                                  value={day.endTime}
                                  type="time"
                                  for={`endTime-${day.day}`}
                                />
                              </div>
                              <div className="col-md-4 col-12 mb-3">
                                <div className="iran-sans mb-2">
                                  بازه زمانی هر نوبت رزرو
                                </div>
                                <FormControl fullWidth className="form-blue">
                                  <Select
                                    labelId={`interval-label-${day.day}`}
                                    value={day.intervalMinutes}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        index,
                                        "intervalMinutes",
                                        e.target.value,
                                      )
                                    }
                                    sx={{
                                      height: "37px",
                                      fontFamily:
                                        "iran-sans, MyPersianFont, sans-serif",
                                    }}
                                  >
                                    {intervalOptions.map((interval) => (
                                      <MenuItem key={interval} value={interval}>
                                        {interval} دقیقه
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </div>
                            </div>
                          ) : (
                            <span className="iran-sans">غیر فعال</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="app-setting-save">
                <Button
                  variant="contained"
                  className="px-5 btn-blue iran-sans"
                  onClick={submitWorkingDaysHandler}
                  disabled={!workingDaysChangeDetection()}
                >
                  <SaveIcon sx={{ fontSize: 18, ml: 1 }} />
                  <span>ذخیره تغییرات</span>
                </Button>
              </div>
            </>
          )}

          {/* Tab 3: Exception Days */}
          {activeTab === 2 && (
            <>
              <div className="row mt-2">
                <div className="col-12">
                  <div className="card app-setting-panel">
                    <div className="card-body">
                      <SettingsSectionTitle icon={EventBusyIcon}>
                        مدیریت روزهای استثنا
                      </SettingsSectionTitle>

                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0 iran-sans">انتخاب تاریخ‌ها</h6>
                          <small className="text-muted iran-sans">
                            {selectedDates.length} تاریخ انتخاب شده
                          </small>
                        </div>

                        <DatePicker
                          multiple
                          calendar={persian}
                          locale={persian_fa}
                          value={selectedDates.map((date) =>
                            new DateObject({
                              date,
                              calendar: gregorian,
                              locale: gregorian_en,
                            }).convert(persian, persian_fa),
                          )}
                          onChange={(d) => {
                            const pickedDates = (
                              Array.isArray(d) ? d : d ? [d] : []
                            )
                              .map((item) => toYMD(item))
                              .filter(
                                (date) => date && !exceptionSet.has(date),
                              );
                            setSelectedDates(Array.from(new Set(pickedDates)));
                          }}
                          minDate={new DateObject()}
                          mapDays={mapDays}
                          inputClass="form-control iran-sans"
                          className="w-100"
                          style={{
                            width: "100%",
                          }}
                          placeholder="تاریخ‌ها را انتخاب کنید"
                        />

                        {selectedDates.length > 0 && (
                          <div className="mt-3 d-flex flex-wrap gap-2">
                            {selectedDates.map((date) => (
                              <Chip
                                key={date}
                                label={formatYmdToFa(date)}
                                onDelete={() => removeSelectedDate(date)}
                                className="iran-sans"
                                sx={{
                                  backgroundColor: "rgba(124, 200, 144, 0.18)",
                                  color: "#053251",
                                  fontWeight: 700,
                                  borderRadius: "10px",
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="row mb-4">
                        <div className="col-md-8 col-12 mb-3">
                          <FormInput
                            className="form-blue"
                            name="توضیحات (اختیاری)"
                            onChange={(e) => setExceptionReason(e.target.value)}
                            value={exceptionReason}
                            type="text"
                            for="exceptionReason"
                          />
                        </div>
                        <div className="col-md-2 col-12 mb-3">
                          <Button
                            variant="outlined"
                            className="w-100"
                            onClick={() => setSelectedDates([])}
                            disabled={selectedDates.length === 0}
                            style={{ marginTop: "32px" }}
                          >
                            <span>پاک کردن</span>
                          </Button>
                        </div>
                        <div className="col-md-2 col-12 mb-3">
                          <Button
                            variant="contained"
                            className="w-100 btn-blue"
                            onClick={handleAddException}
                            disabled={selectedDates.length === 0}
                            style={{ marginTop: "32px" }}
                          >
                            <span>افزودن روز تعطیل</span>
                          </Button>
                        </div>
                      </div>

                      <hr className="my-4" />

                      <h6 className="fw-bold color-blue mb-3 iran-sans">
                        روزهای تعطیل ثبت شده
                      </h6>

                      {exceptionDays.length > 0 ? (
                        <div className="row">
                          {exceptionDays.map((exception) => (
                            <div key={exception.id} className="col-12 mb-3">
                              <div className="exception-day-item">
                                <div className="iran-sans">
                                  <div className="fw-bold color-blue">
                                    تاریخ: {exception.dateDisplay}
                                  </div>
                                  {exception.reason && (
                                    <div className="text-muted small">
                                      توضیحات: {exception.reason}
                                    </div>
                                  )}
                                </div>
                                <IconButton
                                  onClick={() =>
                                    handleDeleteException(exception.id)
                                  }
                                  size="small"
                                  sx={{ color: "#dc3545" }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted iran-sans">
                          هیچ روز تعطیلی ثبت نشده است.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ✅ Tab 4: محتوای سایت */}
          {activeTab === 3 && tenantContent && (
            <>
              <div className="row mt-2">
                <div className="col-12">
                  <div className="content-tab-intro iran-sans">
                    <div>
                      <h3>محتوای سایت</h3>
                      <p>
                        متن‌ها، تصاویر، بخش‌های قابل نمایش و مودال‌های سایت را
                        از اینجا مدیریت کنید. هر بخش را باز کنید و بعد از ویرایش
                        ذخیره کنید.
                      </p>
                    </div>
                  </div>

                  <div className="content-tab-group">
                    <div className="content-tab-group-label iran-sans">
                      سئو و ظاهر
                    </div>
                    {/* SEO Settings */}
                    {CheckRole(["Admin"]) && (
                      <Accordion
                        sx={contentAccordionSx}
                        className="settings-accordion"
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={contentAccordionSummarySx}
                        >
                          <AccordionTitle
                            icon={SearchIcon}
                            title="تنظیمات SEO"
                            hint="نام سایت، متا و اشتراک‌گذاری در شبکه‌های اجتماعی"
                          />
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3 }}>
                          <div className="row">
                            <div className="col-md-6 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="نام سایت"
                                onChange={(e) =>
                                  handleContentChange(
                                    "siteName",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.siteName || ""}
                                for="siteName"
                              />
                            </div>

                            <div className="col-md-6 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="عنوان پیش‌فرض صفحات"
                                onChange={(e) =>
                                  handleContentChange(
                                    "defaultPageTitle",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.defaultPageTitle || ""}
                                for="defaultPageTitle"
                              />
                            </div>

                            <div className="col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="توضیحات سایت (Meta Description)"
                                type="textarea"
                                onChange={(e) =>
                                  handleContentChange(
                                    "siteDescription",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.siteDescription || ""}
                                for="siteDescription"
                                height="100px"
                              />
                            </div>

                            <div className="col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="کلمات کلیدی (Keywords)"
                                onChange={(e) =>
                                  handleContentChange(
                                    "siteKeywords",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.siteKeywords || ""}
                                for="siteKeywords"
                              />
                            </div>

                            <div className="col-md-6 col-12 mb-3">
                              <ImageUploadField
                                label="تصویر Open Graph (اشتراک‌گذاری در شبکه‌های اجتماعی)"
                                field="ogImage"
                                value={tenantContent.ogImage || ""}
                                onUploaded={(url) =>
                                  handleImageUploaded("ogImage", url)
                                }
                                onError={(message) => {
                                  setSnackbarMessage(message);
                                  setSnackbarSeverity("error");
                                  setOpenSnackbar(true);
                                }}
                              />
                            </div>

                            <div className="col-md-6 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="URL کانونیکال (Canonical)"
                                onChange={(e) =>
                                  handleContentChange(
                                    "canonicalUrl",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.canonicalUrl || ""}
                                for="canonicalUrl"
                                dir="ltr"
                              />
                            </div>

                            <div className="col-md-6 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="عنوان صفحه درباره"
                                onChange={(e) =>
                                  handleContentChange(
                                    "aboutPageTitle",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.aboutPageTitle || ""}
                                for="aboutPageTitle"
                                placeholder={`درباره دکتر ${tenantContent.doctorName || ""}`}
                              />
                            </div>
                          </div>

                          <hr className="my-4" />
                          <h6 className="fw-bold color-blue mb-2 iran-sans">
                            اینماد
                          </h6>
                          <p
                            className="iran-sans mb-3"
                            style={{
                              fontSize: "13px",
                              color: "#b42318",
                              lineHeight: 1.8,
                            }}
                          >
                            فقط کد رسمی همین دامنه را وارد کنید. کد اینماد سایت
                            پزشک دیگر را نگذارید؛ اینماد دامنه را بررسی می‌کند
                            و استفاده از کد اشتباه می‌تواند نماد هر دو سایت را
                            باطل کند. اگر هنوز اینماد این دامنه صادر نشده، این
                            فیلدها را خالی بگذارید تا نشانه‌ای نمایش داده نشود.
                          </p>
                          <div className="row">
                            <div className="col-md-4 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="متا اینماد (content)"
                                onChange={(e) =>
                                  handleContentChange(
                                    "enamadMeta",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.enamadMeta || ""}
                                for="enamadMeta"
                                dir="ltr"
                                placeholder="مثلاً 75376836"
                              />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="شناسه اینماد (id)"
                                onChange={(e) =>
                                  handleContentChange("enamadId", e.target.value)
                                }
                                value={tenantContent.enamadId || ""}
                                for="enamadId"
                                dir="ltr"
                                placeholder="مثلاً 537747"
                              />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="کد اینماد (Code)"
                                onChange={(e) =>
                                  handleContentChange(
                                    "enamadCode",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.enamadCode || ""}
                                for="enamadCode"
                                dir="ltr"
                              />
                            </div>
                          </div>

                          {/* SEO صفحه مشاوره */}
                          <hr className="my-4" />
                          <h6 className="fw-bold color-blue mb-3 iran-sans">
                            صفحه مشاوره آنلاین
                          </h6>

                          <div className="row">
                            <div className="col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="عنوان صفحه (Page Title)"
                                onChange={(e) =>
                                  handleContentChange(
                                    "counselingPageTitle",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.counselingPageTitle || ""}
                                for="counselingPageTitle"
                                placeholder={`مشاوره آنلاین | ${tenantContent.siteName}`}
                              />
                            </div>

                            <div className="col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="توضیحات صفحه (Meta Description)"
                                type="textarea"
                                onChange={(e) =>
                                  handleContentChange(
                                    "counselingPageDescription",
                                    e.target.value,
                                  )
                                }
                                value={
                                  tenantContent.counselingPageDescription || ""
                                }
                                for="counselingPageDescription"
                                height="100px"
                              />
                            </div>

                            <div className="col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="کلمات کلیدی (Keywords)"
                                onChange={(e) =>
                                  handleContentChange(
                                    "counselingPageKeywords",
                                    e.target.value,
                                  )
                                }
                                value={
                                  tenantContent.counselingPageKeywords || ""
                                }
                                for="counselingPageKeywords"
                              />
                            </div>

                            <div className="col-12 mb-3">
                              <ImageUploadField
                                label="تصویر Open Graph صفحه مشاوره (اختیاری)"
                                field="counselingOgImage"
                                value={tenantContent.counselingOgImage || ""}
                                onUploaded={(url) =>
                                  handleImageUploaded("counselingOgImage", url)
                                }
                                onError={(message) => {
                                  setSnackbarMessage(message);
                                  setSnackbarSeverity("error");
                                  setOpenSnackbar(true);
                                }}
                              />
                            </div>
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* ✅ نمایش بخش‌های صفحه */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={VisibilityIcon}
                          title="نمایش بخش‌های صفحه اصلی"
                          hint="کنترل نمایش بلوک‌های صفحه اصلی"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="row">
                          {[
                            {
                              key: "showHeroSection",
                              label: "نمایش بخش Hero",
                            },
                            {
                              key: "showAboutSection",
                              label: "نمایش بخش درباره پزشک",
                            },
                            {
                              key: "showHomeInitialModal",
                              label: "نمایش مودال اولیه",
                            },
                            {
                              key: "showBlogSection",
                              label: "نمایش بخش مقالات",
                            },
                            {
                              key: "showReservationSection",
                              label: "نمایش بخش رزرو نوبت",
                            },
                            {
                              key: "showServicesSection",
                              label: "نمایش بخش خدمات",
                            },
                            {
                              key: "showCommentsSection",
                              label: "نمایش بخش نظرات",
                            },
                            {
                              key: "showAvailibaleTimeCount",
                              label:
                                "نمایش تعداد نوبت‌های قابل رزرو به ازای هر روز",
                            },
                            {
                              key: "showPortfolioSection",
                              label: "نمایش بخش نمونه کارها",
                            },
                            { key: "showFooter", label: "نمایش فوتر" },
                          ].map((item) => (
                            <div
                              key={item.key}
                              className="col-md-6 col-12 mb-3"
                            >
                              <div className="visibility-toggle">
                                <Switch
                                  checked={tenantContent[item.key] || false}
                                  onChange={(e) =>
                                    handleContentChange(
                                      item.key,
                                      e.target.checked,
                                    )
                                  }
                                  color="primary"
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                      color: "#7cc890",
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                      {
                                        backgroundColor: "#7cc890",
                                      },
                                  }}
                                />
                                <span className="iran-sans me-2">
                                  {item.label}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ تنظیمات ظاهری */}
                    {CheckRole(["Admin"]) && (
                      <Accordion
                        sx={contentAccordionSx}
                        className="settings-accordion"
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={contentAccordionSummarySx}
                        >
                          <AccordionTitle
                            icon={PaletteIcon}
                            title="تنظیمات ظاهری"
                            hint="رنگ‌ها، لوگو و Favicon"
                          />
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3 }}>
                          <div className="row">
                            <div className="col-md-6 col-12 mb-3">
                              <label className="form-label iran-sans">
                                رنگ اصلی (Primary)
                              </label>
                              <input
                                type="color"
                                className="form-control form-control-color"
                                value={tenantContent.primaryColor || "#053251"}
                                onChange={(e) =>
                                  handleContentChange(
                                    "primaryColor",
                                    e.target.value,
                                  )
                                }
                                style={{ height: "50px" }}
                              />
                            </div>

                            <div className="col-md-6 col-12 mb-3">
                              <label className="form-label iran-sans">
                                رنگ ثانویه (Secondary)
                              </label>
                              <input
                                type="color"
                                className="form-control form-control-color"
                                value={
                                  tenantContent.secondaryColor || "#7cc890"
                                }
                                onChange={(e) =>
                                  handleContentChange(
                                    "secondaryColor",
                                    e.target.value,
                                  )
                                }
                                style={{ height: "50px" }}
                              />
                            </div>

                            <div className="col-md-6 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="آدرس لوگو"
                                onChange={(e) =>
                                  handleContentChange("logoUrl", e.target.value)
                                }
                                value={tenantContent.logoUrl || ""}
                                for="logoUrl"
                                dir="ltr"
                              />
                            </div>

                            <div className="col-md-6 col-12 mb-3">
                              <FormInput
                                className="form-blue"
                                name="آدرس Favicon"
                                onChange={(e) =>
                                  handleContentChange(
                                    "faviconUrl",
                                    e.target.value,
                                  )
                                }
                                value={tenantContent.faviconUrl || ""}
                                for="faviconUrl"
                                dir="ltr"
                              />
                            </div>
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </div>

                  <div className="content-tab-group">
                    <div className="content-tab-group-label iran-sans">
                      پزشک و ارتباط
                    </div>
                    {/* اطلاعات پزشک */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={PersonIcon}
                          title="اطلاعات پزشک"
                          hint="نام، تخصص و اطلاعات تماس"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="row">
                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="نام پزشک"
                              onChange={(e) =>
                                handleContentChange(
                                  "doctorName",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.doctorName || ""}
                              for="doctorName"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="نام پزشک (انگلیسی)"
                              onChange={(e) =>
                                handleContentChange(
                                  "doctorNameEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.doctorNameEn || ""}
                              for="doctorNameEn"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="عنوان پزشک"
                              onChange={(e) =>
                                handleContentChange(
                                  "doctorTitle",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.doctorTitle || ""}
                              for="doctorTitle"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="عنوان پزشک (انگلیسی)"
                              onChange={(e) =>
                                handleContentChange(
                                  "doctorTitleEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.doctorTitleEn || ""}
                              for="doctorTitleEn"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="تخصص پزشک"
                              onChange={(e) =>
                                handleContentChange(
                                  "doctorSpecialty",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.doctorSpecialty || ""}
                              for="doctorSpecialty"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="تخصص پزشک (انگلیسی)"
                              onChange={(e) =>
                                handleContentChange(
                                  "doctorSpecialtyEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.doctorSpecialtyEn || ""}
                              for="doctorSpecialtyEn"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              type="textarea"
                              name="شماره تماس مطب"
                              onChange={(e) =>
                                handleContentChange(
                                  "phoneNumber",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.phoneNumber || ""}
                              for="phoneNumber"
                              height="60px"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="ایمیل"
                              onChange={(e) =>
                                handleContentChange("email", e.target.value)
                              }
                              value={tenantContent.email || ""}
                              for="email"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="آدرس مطب"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange("address", e.target.value)
                              }
                              value={tenantContent.address || ""}
                              for="address"
                              height="80px"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="آدرس مطب (انگلیسی)"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "addressEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.addressEn || ""}
                              for="addressEn"
                              height="80px"
                              dir="ltr"
                            />
                          </div>

                          {CheckRole(["Admin"]) && (
                            <>
                              <div className="col-12 mb-3">
                                <FormInput
                                  className="form-blue"
                                  name="لینک لوکیشن گوگل مپ"
                                  onChange={(e) =>
                                    handleContentChange(
                                      "clinicLocation",
                                      e.target.value,
                                    )
                                  }
                                  value={tenantContent.clinicLocation || ""}
                                  for="clinicLocation"
                                  dir="ltr"
                                />
                              </div>

                              <div className="col-12 mb-3">
                                <FormInput
                                  className="form-blue"
                                  name="مختصات نقشه (Latitude, Longitude)"
                                  onChange={(e) =>
                                    handleContentChange(
                                      "mapCoordinates",
                                      e.target.value,
                                    )
                                  }
                                  value={tenantContent.mapCoordinates || ""}
                                  for="mapCoordinates"
                                  dir="ltr"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ شبکه‌های اجتماعی */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={ShareIcon}
                          title="شبکه‌های اجتماعی"
                          hint="واتساپ، تلگرام و اینستاگرام"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="row">
                          <div className="col-md-4 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="شماره واتساپ"
                              onChange={(e) =>
                                handleContentChange(
                                  "whatsappNumber",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.whatsappNumber || ""}
                              for="whatsappNumber"
                              placeholder="989123456789"
                            />
                          </div>

                          <div className="col-md-4 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="لینک تلگرام"
                              onChange={(e) =>
                                handleContentChange(
                                  "telegramUrl",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.telegramUrl || ""}
                              for="telegramUrl"
                              dir="ltr"
                              placeholder="https://t.me/username"
                            />
                          </div>

                          <div className="col-md-4 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="لینک اینستاگرام"
                              onChange={(e) =>
                                handleContentChange(
                                  "instagramUrl",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.instagramUrl || ""}
                              for="instagramUrl"
                              dir="ltr"
                              placeholder="https://instagram.com/username"
                            />
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>

                  <div className="content-tab-group">
                    <div className="content-tab-group-label iran-sans">
                      محتوای صفحات
                    </div>
                    {/* محتوای سفارشی - صفحه اصلی */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={HomeIcon}
                          title="محتوای صفحه اصلی"
                          hint="عنوان Hero، تصویر و آمار"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="row">
                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="عنوان Hero"
                              onChange={(e) =>
                                handleContentChange("heroTitle", e.target.value)
                              }
                              value={tenantContent.heroTitle || ""}
                              for="heroTitle"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="زیرعنوان Hero"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "heroSubtitle",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.heroSubtitle || ""}
                              for="heroSubtitle"
                              height="80px"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <ImageUploadField
                              label="تصویر پزشک در بخش Hero"
                              field="doctorImageUrl"
                              value={tenantContent.doctorImageUrl || ""}
                              onUploaded={(url) =>
                                handleImageUploaded("doctorImageUrl", url)
                              }
                              onError={(message) => {
                                setSnackbarMessage(message);
                                setSnackbarSeverity("error");
                                setOpenSnackbar(true);
                              }}
                              hint="این تصویر روی سرور ذخیره می‌شود و در صفحه اصلی نمایش داده می‌شود. JPG، PNG یا WEBP — حداکثر ۵ مگابایت"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="متن بخش درباره پزشک"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "homeAboutSectionText",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.homeAboutSectionText || ""}
                              for="homeAboutSectionText"
                              height="120px"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="متن بخش درباره پزشک (انگلیسی)"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "homeAboutSectionTextEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.homeAboutSectionTextEn || ""}
                              for="homeAboutSectionTextEn"
                              height="120px"
                              dir="ltr"
                            />
                          </div>

                          <div className="row">
                            {[
                              {
                                key: "showOnlineCounselingCount",
                                label: "نمایش تعداد مشاوره آنلاین",
                              },
                              {
                                key: "showYearExprience",
                                label: "نمایش تجربه پزشک",
                              },
                              {
                                key: "showPatientCount",
                                label: "نمایش تعداد بیماران",
                              },
                            ].map((item) => (
                              <div
                                key={item.key}
                                className="col-md-6 col-12 mb-3"
                              >
                                <div className="visibility-toggle">
                                  <Switch
                                    checked={tenantContent[item.key] || false}
                                    onChange={(e) =>
                                      handleContentChange(
                                        item.key,
                                        e.target.checked,
                                      )
                                    }
                                    color="primary"
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#7cc890",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          backgroundColor: "#7cc890",
                                        },
                                    }}
                                  />
                                  <span className="iran-sans me-2">
                                    {item.label}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="تجربه پزشک (سال)"
                              onChange={(e) =>
                                handleContentChange(
                                  "yearExprience",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.yearExprience || ""}
                              for="yearExprience"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="تعداد بیمار"
                              onChange={(e) =>
                                handleContentChange(
                                  "patientCount",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.patientCount || ""}
                              for="patientCount"
                            />
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ صفحه درباره پزشک */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={DescriptionIcon}
                          title="صفحه درباره پزشک"
                          hint="متن، تصویر و آمار صفحه درباره"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="row">
                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="تعداد مقالات"
                              type="number"
                              onChange={(e) =>
                                handleContentChange(
                                  "publicationsCount",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.publicationsCount || ""}
                              for="publicationsCount"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <ImageUploadField
                              label="تصویر پزشک"
                              field="doctorAvatarUrl"
                              value={tenantContent.doctorAvatarUrl || ""}
                              rounded
                              onUploaded={(url) =>
                                handleImageUploaded("doctorAvatarUrl", url)
                              }
                              onError={(message) => {
                                setSnackbarMessage(message);
                                setSnackbarSeverity("error");
                                setOpenSnackbar(true);
                              }}
                              hint="این تصویر روی سرور ذخیره می‌شود و در صفحه درباره پزشک و مشاوره نمایش داده می‌شود."
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="متن صفحه درباره پزشک"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "aboutPageContent",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.aboutPageContent || ""}
                              for="aboutPageContent"
                              height="100px"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="متن صفحه درباره پزشک (انگلیسی)"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "aboutPageContentEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.aboutPageContentEn || ""}
                              for="aboutPageContentEn"
                              height="100px"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="متن تجربیات کاری"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "experienceText",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.experienceText || ""}
                              for="experienceText"
                              height="100px"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="متن تجربیات کاری (انگلیسی)"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "experienceTextEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.experienceTextEn || ""}
                              for="experienceTextEn"
                              height="100px"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="متن مقالات"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "publicationsText",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.publicationsText || ""}
                              for="publicationsText"
                              height="100px"
                            />
                          </div>

                          <div className="col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="متن مقالات (انگلیسی)"
                              type="textarea"
                              onChange={(e) =>
                                handleContentChange(
                                  "publicationsTextEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.publicationsTextEn || ""}
                              for="publicationsTextEn"
                              height="100px"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="اطلاعات محل کار"
                              onChange={(e) =>
                                handleContentChange(
                                  "workplaceInfo",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.workplaceInfo || ""}
                              for="workplaceInfo"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="اطلاعات محل کار (انگلیسی)"
                              onChange={(e) =>
                                handleContentChange(
                                  "workplaceInfoEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.workplaceInfoEn || ""}
                              for="workplaceInfoEn"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="تلفن محل کار"
                              onChange={(e) =>
                                handleContentChange(
                                  "workplacePhone",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.workplacePhone || ""}
                              for="workplacePhone"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="آدرس مطب"
                              onChange={(e) =>
                                handleContentChange(
                                  "clinicInfo",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.clinicInfo || ""}
                              for="clinicInfo"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="آدرس مطب (انگلیسی)"
                              onChange={(e) =>
                                handleContentChange(
                                  "clinicInfoEn",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.clinicInfoEn || ""}
                              for="clinicInfoEn"
                              dir="ltr"
                            />
                          </div>

                          <div className="col-md-6 col-12 mb-3">
                            <FormInput
                              className="form-blue"
                              name="تلفن مطب"
                              onChange={(e) =>
                                handleContentChange(
                                  "clinicPhone",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.clinicPhone || ""}
                              for="clinicPhone"
                            />
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>

                  <div className="content-tab-group">
                    <div className="content-tab-group-label iran-sans">
                      رزومه و نمونه کار
                    </div>
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={SchoolIcon}
                          title="تحصیلات"
                          hint="مدارک و سوابق تحصیلی"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const newItem = { textFa: "", textEn: "" };
                              setTenantContent((prev) => ({
                                ...prev,
                                educationItems: [
                                  ...(prev.educationItems || []),
                                  newItem,
                                ],
                              }));
                            }}
                          >
                            <span>افزودن مورد</span>
                          </Button>
                        </div>

                        {tenantContent.educationItems &&
                          tenantContent.educationItems.map((item, index) => (
                            <div key={index} className="content-list-card">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-2">
                                  <h6 className="fw-bold color-blue mb-0 iran-sans">
                                    مورد {index + 1}
                                  </h6>

                                  {/* ✅ دکمه‌های Move Up/Down */}
                                  <div className="d-flex gap-1">
                                    <IconButton
                                      onClick={() =>
                                        moveEducationItem(index, "up")
                                      }
                                      disabled={index === 0}
                                      size="small"
                                      sx={{
                                        color: "#7cc890",
                                        "&:disabled": { color: "#ccc" },
                                      }}
                                      title="انتقال به بالا"
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        moveEducationItem(index, "down")
                                      }
                                      disabled={
                                        index ===
                                        tenantContent.educationItems.length - 1
                                      }
                                      size="small"
                                      sx={{
                                        color: "#7cc890",
                                        "&:disabled": { color: "#ccc" },
                                      }}
                                      title="انتقال به پایین"
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                </div>

                                <IconButton
                                  onClick={() => {
                                    const updated =
                                      tenantContent.educationItems.filter(
                                        (_, i) => i !== index,
                                      );
                                    setTenantContent((prev) => ({
                                      ...prev,
                                      educationItems: updated,
                                    }));
                                  }}
                                  size="small"
                                  sx={{ color: "#dc3545" }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>

                              <div className="row">
                                <div className="col-md-6 col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="متن فارسی"
                                    type="textarea"
                                    onChange={(e) => {
                                      const updated = [
                                        ...tenantContent.educationItems,
                                      ];
                                      updated[index].textFa = e.target.value;
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        educationItems: updated,
                                      }));
                                    }}
                                    value={item.textFa || ""}
                                    for={`education-fa-${index}`}
                                    height="80px"
                                  />
                                </div>

                                <div className="col-md-6 col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="متن انگلیسی"
                                    type="textarea"
                                    onChange={(e) => {
                                      const updated = [
                                        ...tenantContent.educationItems,
                                      ];
                                      updated[index].textEn = e.target.value;
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        educationItems: updated,
                                      }));
                                    }}
                                    value={item.textEn || ""}
                                    for={`education-en-${index}`}
                                    height="80px"
                                    dir="ltr"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ رتبه‌های علمی */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={WorkspacePremiumIcon}
                          title="رتبه‌های علمی"
                          hint="جوایز و افتخارات علمی"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setTenantContent((prev) => ({
                                ...prev,
                                academicDegrees: [
                                  ...(prev.academicDegrees || []),
                                  { textFa: "", textEn: "" },
                                ],
                              }));
                            }}
                          >
                            <span>افزودن رتبه</span>
                          </Button>
                        </div>

                        {tenantContent.academicDegrees &&
                          tenantContent.academicDegrees.map((degree, index) => (
                            <div key={index} className="content-list-card">
                              <div className="d-flex justify-content-between align-items-center">
                                <div
                                  className="d-flex align-items-center gap-2"
                                  style={{ flex: 1 }}
                                >
                                  {/* ✅ دکمه‌های Move */}
                                  <div className="d-flex gap-1">
                                    <IconButton
                                      onClick={() =>
                                        moveAcademicDegree(index, "up")
                                      }
                                      disabled={index === 0}
                                      size="small"
                                      sx={{
                                        color: "#7cc890",
                                        "&:disabled": { color: "#ccc" },
                                      }}
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        moveAcademicDegree(index, "down")
                                      }
                                      disabled={
                                        index ===
                                        tenantContent.academicDegrees.length - 1
                                      }
                                      size="small"
                                      sx={{
                                        color: "#7cc890",
                                        "&:disabled": { color: "#ccc" },
                                      }}
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                  </div>

                                  <div style={{ flex: 1, marginRight: "10px" }}>
                                    <FormInput
                                      className="form-blue"
                                      name={`رتبه علمی ${index + 1}`}
                                      onChange={(e) =>
                                        updateLocalizedListItem(
                                          "academicDegrees",
                                          index,
                                          "textFa",
                                          e.target.value,
                                        )
                                      }
                                      value={asLocalizedItem(degree).textFa}
                                      for={`academic-${index}`}
                                    />
                                    <FormInput
                                      className="form-blue mt-2"
                                      name={`رتبه علمی ${index + 1} (انگلیسی)`}
                                      onChange={(e) =>
                                        updateLocalizedListItem(
                                          "academicDegrees",
                                          index,
                                          "textEn",
                                          e.target.value,
                                        )
                                      }
                                      value={asLocalizedItem(degree).textEn}
                                      for={`academic-en-${index}`}
                                      dir="ltr"
                                    />
                                  </div>
                                </div>

                                <IconButton
                                  onClick={() => {
                                    const updated =
                                      tenantContent.academicDegrees.filter(
                                        (_, i) => i !== index,
                                      );
                                    setTenantContent((prev) => ({
                                      ...prev,
                                      academicDegrees: updated,
                                    }));
                                  }}
                                  size="small"
                                  sx={{ color: "#dc3545" }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            </div>
                          ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ مسئولیت‌های اجرایی */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={WorkIcon}
                          title="مسئولیت‌های اجرایی"
                          hint="سمت‌ها و سوابق اجرایی"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setTenantContent((prev) => ({
                                ...prev,
                                responsibilities: [
                                  ...(prev.responsibilities || []),
                                  { textFa: "", textEn: "" },
                                ],
                              }));
                            }}
                          >
                            <span>افزودن مسئولیت</span>
                          </Button>
                        </div>

                        {tenantContent.responsibilities &&
                          tenantContent.responsibilities.map(
                            (responsibility, index) => (
                              <div key={index} className="content-list-card">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div
                                    className="d-flex align-items-center gap-2"
                                    style={{ flex: 1 }}
                                  >
                                    {/* ✅ دکمه‌های Move */}
                                    <div className="d-flex gap-1">
                                      <IconButton
                                        onClick={() =>
                                          moveResponsibility(index, "up")
                                        }
                                        disabled={index === 0}
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowUpwardIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        onClick={() =>
                                          moveResponsibility(index, "down")
                                        }
                                        disabled={
                                          index ===
                                          tenantContent.responsibilities
                                            .length -
                                            1
                                        }
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowDownwardIcon fontSize="small" />
                                      </IconButton>
                                    </div>

                                    <div
                                      style={{ flex: 1, marginRight: "10px" }}
                                    >
                                      <FormInput
                                        className="form-blue"
                                        name={`مسئولیت ${index + 1}`}
                                        type="textarea"
                                        onChange={(e) =>
                                          updateLocalizedListItem(
                                            "responsibilities",
                                            index,
                                            "textFa",
                                            e.target.value,
                                          )
                                        }
                                        value={
                                          asLocalizedItem(responsibility)
                                            .textFa
                                        }
                                        for={`responsibility-${index}`}
                                        height="60px"
                                      />
                                      <FormInput
                                        className="form-blue mt-2"
                                        name={`مسئولیت ${index + 1} (انگلیسی)`}
                                        type="textarea"
                                        onChange={(e) =>
                                          updateLocalizedListItem(
                                            "responsibilities",
                                            index,
                                            "textEn",
                                            e.target.value,
                                          )
                                        }
                                        value={
                                          asLocalizedItem(responsibility)
                                            .textEn
                                        }
                                        for={`responsibility-en-${index}`}
                                        height="60px"
                                        dir="ltr"
                                      />
                                    </div>
                                  </div>

                                  <IconButton
                                    onClick={() => {
                                      const updated =
                                        tenantContent.responsibilities.filter(
                                          (_, i) => i !== index,
                                        );
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        responsibilities: updated,
                                      }));
                                    }}
                                    size="small"
                                    sx={{ color: "#dc3545" }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </div>
                              </div>
                            ),
                          )}
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ خدمات */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={MedicalServicesIcon}
                          title="خدمات"
                          hint="خدمات قابل ارائه در سایت"
                        />
                      </AccordionSummary>

                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={handleAddService}
                          >
                            <span>افزودن خدمت</span>
                          </Button>
                        </div>

                        {tenantContent.services &&
                          tenantContent.services.map((service, index) => (
                            <div key={index} className="content-list-card">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-2">
                                  <h6 className="fw-bold color-blue mb-0 iran-sans">
                                    خدمت {index + 1}
                                  </h6>

                                  {/* ✅ دکمه‌های Move */}
                                  <div className="d-flex gap-1">
                                    <IconButton
                                      onClick={() => moveService(index, "up")}
                                      disabled={index === 0}
                                      size="small"
                                      sx={{
                                        color: "#7cc890",
                                        "&:disabled": { color: "#ccc" },
                                      }}
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      onClick={() => moveService(index, "down")}
                                      disabled={
                                        index ===
                                        tenantContent.services.length - 1
                                      }
                                      size="small"
                                      sx={{
                                        color: "#7cc890",
                                        "&:disabled": { color: "#ccc" },
                                      }}
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                </div>

                                <IconButton
                                  onClick={() => handleDeleteService(index)}
                                  size="small"
                                  sx={{ color: "#dc3545" }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>

                              <div className="row">
                                <div className="col-md-6 col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="عنوان"
                                    onChange={(e) =>
                                      handleServiceChange(
                                        index,
                                        "title",
                                        e.target.value,
                                      )
                                    }
                                    value={service.title || ""}
                                    for={`service-title-${index}`}
                                  />
                                </div>

                                <div className="col-md-6 col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="آدرس آیکون"
                                    onChange={(e) =>
                                      handleServiceChange(
                                        index,
                                        "iconUrl",
                                        e.target.value,
                                      )
                                    }
                                    value={service.iconUrl || ""}
                                    for={`service-icon-${index}`}
                                    dir="ltr"
                                  />
                                </div>

                                <div className="col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="توضیحات کوتاه"
                                    type="textarea"
                                    onChange={(e) =>
                                      handleServiceChange(
                                        index,
                                        "description",
                                        e.target.value,
                                      )
                                    }
                                    value={service.description || ""}
                                    for={`service-desc-${index}`}
                                    height="80px"
                                  />
                                </div>

                                <div className="col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="توضیحات کامل"
                                    type="textarea"
                                    onChange={(e) =>
                                      handleServiceChange(
                                        index,
                                        "fullDescription",
                                        e.target.value,
                                      )
                                    }
                                    value={service.fullDescription || ""}
                                    for={`service-full-${index}`}
                                    height="120px"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                    {/* ✅ نمونه کارها (Portfolio) */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={CollectionsIcon}
                          title="نمونه کارها"
                          hint="گالری تصاویر و پروژه‌ها"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const newItem = {
                                imageUrl: "",
                                title: "",
                                description: "",
                              };
                              setTenantContent((prev) => ({
                                ...prev,
                                portfolioItems: [
                                  ...(prev.portfolioItems || []),
                                  newItem,
                                ],
                              }));
                            }}
                          >
                            <span>افزودن نمونه کار</span>
                          </Button>
                        </div>

                        {tenantContent.portfolioItems &&
                          tenantContent.portfolioItems.map((item, index) => (
                            <div key={index} className="content-list-card">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-2">
                                  <h6 className="fw-bold color-blue mb-0 iran-sans">
                                    نمونه کار {index + 1}
                                  </h6>

                                  <div className="d-flex gap-1">
                                    <IconButton
                                      onClick={() =>
                                        movePortfolioItem(index, "up")
                                      }
                                      disabled={index === 0}
                                      size="small"
                                      sx={{
                                        color: "#7cc890",
                                        "&:disabled": { color: "#ccc" },
                                      }}
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        movePortfolioItem(index, "down")
                                      }
                                      disabled={
                                        index ===
                                        tenantContent.portfolioItems.length - 1
                                      }
                                      size="small"
                                      sx={{
                                        color: "#7cc890",
                                        "&:disabled": { color: "#ccc" },
                                      }}
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                </div>

                                <IconButton
                                  onClick={() => {
                                    const updated =
                                      tenantContent.portfolioItems.filter(
                                        (_, i) => i !== index,
                                      );
                                    setTenantContent((prev) => ({
                                      ...prev,
                                      portfolioItems: updated,
                                    }));
                                  }}
                                  size="small"
                                  sx={{ color: "#dc3545" }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>

                              <div className="row">
                                <div className="col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="آدرس تصویر"
                                    onChange={(e) => {
                                      const updated = [
                                        ...tenantContent.portfolioItems,
                                      ];
                                      updated[index].imageUrl = e.target.value;
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        portfolioItems: updated,
                                      }));
                                    }}
                                    value={item.imageUrl || ""}
                                    for={`portfolio-image-${index}`}
                                    dir="ltr"
                                  />
                                </div>

                                <div className="col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="عنوان (اختیاری)"
                                    onChange={(e) => {
                                      const updated = [
                                        ...tenantContent.portfolioItems,
                                      ];
                                      updated[index].title = e.target.value;
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        portfolioItems: updated,
                                      }));
                                    }}
                                    value={item.title || ""}
                                    for={`portfolio-title-${index}`}
                                  />
                                </div>

                                <div className="col-12 mb-3">
                                  <FormInput
                                    className="form-blue"
                                    name="توضیحات (اختیاری)"
                                    type="textarea"
                                    onChange={(e) => {
                                      const updated = [
                                        ...tenantContent.portfolioItems,
                                      ];
                                      updated[index].description =
                                        e.target.value;
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        portfolioItems: updated,
                                      }));
                                    }}
                                    value={item.description || ""}
                                    for={`portfolio-desc-${index}`}
                                    height="80px"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ نظرات کاربران (Testimonials) */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={ForumIcon}
                          title="نظرات کاربران"
                          hint="نظرات نمایش‌داده‌شده در سایت"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const newItem = {
                                name: "",
                                text: "",
                              };
                              setTenantContent((prev) => ({
                                ...prev,
                                testimonials: [
                                  ...(prev.testimonials || []),
                                  newItem,
                                ],
                              }));
                            }}
                          >
                            <span>افزودن نظر</span>
                          </Button>
                        </div>

                        {tenantContent.testimonials &&
                          tenantContent.testimonials.map(
                            (testimonial, index) => (
                              <div key={index} className="content-list-card">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div className="d-flex align-items-center gap-2">
                                    <h6 className="fw-bold color-blue mb-0 iran-sans">
                                      نظر {index + 1}
                                    </h6>

                                    <div className="d-flex gap-1">
                                      <IconButton
                                        onClick={() =>
                                          moveTestimonial(index, "up")
                                        }
                                        disabled={index === 0}
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowUpwardIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        onClick={() =>
                                          moveTestimonial(index, "down")
                                        }
                                        disabled={
                                          index ===
                                          tenantContent.testimonials.length - 1
                                        }
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowDownwardIcon fontSize="small" />
                                      </IconButton>
                                    </div>
                                  </div>

                                  <IconButton
                                    onClick={() => {
                                      const updated =
                                        tenantContent.testimonials.filter(
                                          (_, i) => i !== index,
                                        );
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        testimonials: updated,
                                      }));
                                    }}
                                    size="small"
                                    sx={{ color: "#dc3545" }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </div>

                                <div className="row">
                                  <div className="col-md-4 col-12 mb-3">
                                    <FormInput
                                      className="form-blue"
                                      name="نام"
                                      onChange={(e) => {
                                        const updated = [
                                          ...tenantContent.testimonials,
                                        ];
                                        updated[index].name = e.target.value;
                                        setTenantContent((prev) => ({
                                          ...prev,
                                          testimonials: updated,
                                        }));
                                      }}
                                      value={testimonial.name || ""}
                                      for={`testimonial-name-${index}`}
                                    />
                                  </div>

                                  <div className="col-md-8 col-12 mb-3">
                                    <FormInput
                                      className="form-blue"
                                      name="متن نظر"
                                      type="textarea"
                                      onChange={(e) => {
                                        const updated = [
                                          ...tenantContent.testimonials,
                                        ];
                                        updated[index].text = e.target.value;
                                        setTenantContent((prev) => ({
                                          ...prev,
                                          testimonials: updated,
                                        }));
                                      }}
                                      value={testimonial.text || ""}
                                      for={`testimonial-text-${index}`}
                                      height="100px"
                                    />
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                      </AccordionDetails>
                    </Accordion>
                  </div>

                  <div className="content-tab-group">
                    <div className="content-tab-group-label iran-sans">
                      مودال‌ها
                    </div>
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={AssignmentIcon}
                          title="مودال راهنمای رزرو"
                          hint="پیام راهنما قبل از رزرو نوبت"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="row mb-3">
                          <div className="col-12">
                            <FormInput
                              className="form-blue"
                              name="عنوان مودال"
                              onChange={(e) =>
                                handleContentChange(
                                  "reserveInfoModalTitle",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.reserveInfoModalTitle || ""}
                              for="reserveInfoModalTitle"
                            />
                          </div>
                        </div>

                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const newItem = { text: "", style: "normal" };
                              setTenantContent((prev) => ({
                                ...prev,
                                reserveInfoModalContent: [
                                  ...(prev.reserveInfoModalContent || []),
                                  newItem,
                                ],
                              }));
                            }}
                          >
                            <span>افزودن خط</span>
                          </Button>
                        </div>

                        {tenantContent.reserveInfoModalContent &&
                          tenantContent.reserveInfoModalContent.map(
                            (item, index) => (
                              <div key={index} className="content-list-card">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div className="d-flex align-items-center gap-2">
                                    <h6 className="fw-bold color-blue mb-0 iran-sans">
                                      خط {index + 1}
                                    </h6>

                                    {/* ✅ دکمه‌های Move */}
                                    <div className="d-flex gap-1">
                                      <IconButton
                                        onClick={() =>
                                          moveReserveModalItem(index, "up")
                                        }
                                        disabled={index === 0}
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowUpwardIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        onClick={() =>
                                          moveReserveModalItem(index, "down")
                                        }
                                        disabled={
                                          index ===
                                          tenantContent.reserveInfoModalContent
                                            .length -
                                            1
                                        }
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowDownwardIcon fontSize="small" />
                                      </IconButton>
                                    </div>
                                  </div>

                                  <IconButton
                                    onClick={() => {
                                      const updated =
                                        tenantContent.reserveInfoModalContent.filter(
                                          (_, i) => i !== index,
                                        );
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        reserveInfoModalContent: updated,
                                      }));
                                    }}
                                    size="small"
                                    sx={{ color: "#dc3545" }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </div>

                                <div className="row">
                                  <div className="col-md-8 col-12 mb-3">
                                    <FormInput
                                      className="form-blue"
                                      name="متن"
                                      type="textarea"
                                      onChange={(e) => {
                                        const updated = [
                                          ...tenantContent.reserveInfoModalContent,
                                        ];
                                        updated[index].text = e.target.value;
                                        setTenantContent((prev) => ({
                                          ...prev,
                                          reserveInfoModalContent: updated,
                                        }));
                                      }}
                                      value={item.text || ""}
                                      for={`modal-text-${index}`}
                                      height="100px"
                                    />
                                  </div>

                                  <div className="col-md-4 col-12 mb-3">
                                    <div className="iran-sans mb-2">استایل</div>
                                    <FormControl
                                      fullWidth
                                      className="form-blue"
                                    >
                                      <Select
                                        value={item.style || "normal"}
                                        onChange={(e) => {
                                          const updated = [
                                            ...tenantContent.reserveInfoModalContent,
                                          ];
                                          updated[index].style = e.target.value;
                                          setTenantContent((prev) => ({
                                            ...prev,
                                            reserveInfoModalContent: updated,
                                          }));
                                        }}
                                        sx={{
                                          height: "37px",
                                          fontFamily:
                                            "iran-sans, MyPersianFont, sans-serif",
                                        }}
                                      >
                                        <MenuItem value="normal">عادی</MenuItem>
                                        <MenuItem value="bold">پررنگ</MenuItem>
                                        <MenuItem value="danger">
                                          هشدار (قرمز)
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ مودال راهنمای رزرو */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={AssignmentIcon}
                          title="مودال مشاوره"
                          hint="قبل از پرداخت هزینه مشاوره نمایش داده می‌شود"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const newItem = { text: "", style: "normal" };
                              setTenantContent((prev) => ({
                                ...prev,
                                consultationPaymentModalContent: [
                                  ...(prev.consultationPaymentModalContent ||
                                    []),
                                  newItem,
                                ],
                              }));
                            }}
                          >
                            <span>افزودن خط</span>
                          </Button>
                        </div>

                        {tenantContent.consultationPaymentModalContent &&
                          tenantContent.consultationPaymentModalContent.map(
                            (item, index) => (
                              <div key={index} className="content-list-card">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div className="d-flex align-items-center gap-2">
                                    <h6 className="fw-bold color-blue mb-0 iran-sans">
                                      خط {index + 1}
                                    </h6>

                                    {/* ✅ دکمه‌های Move */}
                                    <div className="d-flex gap-1">
                                      <IconButton
                                        onClick={() =>
                                          moveConsultationModalItem(index, "up")
                                        }
                                        disabled={index === 0}
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowUpwardIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        onClick={() =>
                                          moveConsultationModalItem(
                                            index,
                                            "down",
                                          )
                                        }
                                        disabled={
                                          index ===
                                          tenantContent
                                            .consultationPaymentModalContent
                                            .length -
                                            1
                                        }
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowDownwardIcon fontSize="small" />
                                      </IconButton>
                                    </div>
                                  </div>

                                  <IconButton
                                    onClick={() => {
                                      const updated =
                                        tenantContent.consultationPaymentModalContent.filter(
                                          (_, i) => i !== index,
                                        );
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        consultationPaymentModalContent:
                                          updated,
                                      }));
                                    }}
                                    size="small"
                                    sx={{ color: "#dc3545" }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </div>

                                <div className="row">
                                  <div className="col-md-8 col-12 mb-3">
                                    <FormInput
                                      className="form-blue"
                                      name="متن"
                                      type="textarea"
                                      onChange={(e) => {
                                        const updated = [
                                          ...tenantContent.consultationPaymentModalContent,
                                        ];
                                        updated[index].text = e.target.value;
                                        setTenantContent((prev) => ({
                                          ...prev,
                                          consultationPaymentModalContent:
                                            updated,
                                        }));
                                      }}
                                      value={item.text || ""}
                                      for={`modal-text-${index}`}
                                      height="100px"
                                    />
                                  </div>

                                  <div className="col-md-4 col-12 mb-3">
                                    <div className="iran-sans mb-2">استایل</div>
                                    <FormControl
                                      fullWidth
                                      className="form-blue"
                                    >
                                      <Select
                                        value={item.style || "normal"}
                                        onChange={(e) => {
                                          const updated = [
                                            ...tenantContent.consultationPaymentModalContent,
                                          ];
                                          updated[index].style = e.target.value;
                                          setTenantContent((prev) => ({
                                            ...prev,
                                            consultationPaymentModalContent:
                                              updated,
                                          }));
                                        }}
                                        sx={{
                                          height: "37px",
                                          fontFamily:
                                            "iran-sans, MyPersianFont, sans-serif",
                                        }}
                                      >
                                        <MenuItem value="normal">عادی</MenuItem>
                                        <MenuItem value="bold">پررنگ</MenuItem>
                                        <MenuItem value="danger">
                                          هشدار (قرمز)
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                      </AccordionDetails>
                    </Accordion>

                    {/* ✅ مودال اولیه صفحه اصلی - مشابه بالا */}
                    <Accordion
                      sx={contentAccordionSx}
                      className="settings-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={contentAccordionSummarySx}
                      >
                        <AccordionTitle
                          icon={NotificationsIcon}
                          title="مودال اولیه صفحه اصلی"
                          hint="پیام خوش‌آمد یا اطلاع اولیه هنگام ورود"
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <div className="row mb-3">
                          <div className="col-12">
                            <FormInput
                              className="form-blue"
                              name="عنوان مودال"
                              onChange={(e) =>
                                handleContentChange(
                                  "homeInitialModalTitle",
                                  e.target.value,
                                )
                              }
                              value={tenantContent.homeInitialModalTitle || ""}
                              for="homeInitialModalTitle"
                            />
                          </div>
                        </div>

                        <div className="mb-3 d-flex justify-content-end">
                          <Button
                            variant="contained"
                            className="btn-blue"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const newItem = { text: "", style: "normal" };
                              setTenantContent((prev) => ({
                                ...prev,
                                homeInitialModalContent: [
                                  ...(prev.homeInitialModalContent || []),
                                  newItem,
                                ],
                              }));
                            }}
                          >
                            <span>افزودن خط</span>
                          </Button>
                        </div>

                        {tenantContent.homeInitialModalContent &&
                          tenantContent.homeInitialModalContent.map(
                            (item, index) => (
                              <div key={index} className="content-list-card">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div className="d-flex align-items-center gap-2">
                                    <h6 className="fw-bold color-blue mb-0 iran-sans">
                                      خط {index + 1}
                                    </h6>

                                    {/* ✅ دکمه‌های Move */}
                                    <div className="d-flex gap-1">
                                      <IconButton
                                        onClick={() =>
                                          moveHomeModalItem(index, "up")
                                        }
                                        disabled={index === 0}
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowUpwardIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        onClick={() =>
                                          moveHomeModalItem(index, "down")
                                        }
                                        disabled={
                                          index ===
                                          tenantContent.homeInitialModalContent
                                            .length -
                                            1
                                        }
                                        size="small"
                                        sx={{
                                          color: "#7cc890",
                                          "&:disabled": { color: "#ccc" },
                                        }}
                                      >
                                        <ArrowDownwardIcon fontSize="small" />
                                      </IconButton>
                                    </div>
                                  </div>

                                  <IconButton
                                    onClick={() => {
                                      const updated =
                                        tenantContent.homeInitialModalContent.filter(
                                          (_, i) => i !== index,
                                        );
                                      setTenantContent((prev) => ({
                                        ...prev,
                                        homeInitialModalContent: updated,
                                      }));
                                    }}
                                    size="small"
                                    sx={{ color: "#dc3545" }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </div>

                                <div className="row">
                                  <div className="col-md-8 col-12 mb-3">
                                    <FormInput
                                      className="form-blue"
                                      name="متن"
                                      type="textarea"
                                      onChange={(e) => {
                                        const updated = [
                                          ...tenantContent.homeInitialModalContent,
                                        ];
                                        updated[index].text = e.target.value;
                                        setTenantContent((prev) => ({
                                          ...prev,
                                          homeInitialModalContent: updated,
                                        }));
                                      }}
                                      value={item.text || ""}
                                      for={`modal-text-${index}`}
                                      height="100px"
                                    />
                                  </div>

                                  <div className="col-md-4 col-12 mb-3">
                                    <div className="iran-sans mb-2">استایل</div>
                                    <FormControl
                                      fullWidth
                                      className="form-blue"
                                    >
                                      <Select
                                        value={item.style || "normal"}
                                        onChange={(e) => {
                                          const updated = [
                                            ...tenantContent.homeInitialModalContent,
                                          ];
                                          updated[index].style = e.target.value;
                                          setTenantContent((prev) => ({
                                            ...prev,
                                            homeInitialModalContent: updated,
                                          }));
                                        }}
                                        sx={{
                                          height: "37px",
                                          fontFamily:
                                            "iran-sans, MyPersianFont, sans-serif",
                                        }}
                                      >
                                        <MenuItem value="normal">عادی</MenuItem>
                                        <MenuItem value="bold">پررنگ</MenuItem>
                                        <MenuItem value="danger">
                                          هشدار (قرمز)
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                      </AccordionDetails>
                    </Accordion>
                  </div>
                </div>
              </div>

              <div className="content-tab-save">
                <Button
                  variant="contained"
                  className="px-5 btn-blue iran-sans"
                  onClick={submitTenantContentHandler}
                  disabled={!tenantContentChangeDetection()}
                >
                  <SaveIcon sx={{ fontSize: 18, ml: 1 }} />
                  <span>ذخیره محتوای سایت</span>
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      <Notification
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
      />
    </Content>
  );
};

export default AppSetting;
